<template>
  <AppModal
    :show="show"
    @close="handleClose"
    :title="$t('projects.uploadPhotos')"
    size="medium"
    :show-close="!uploading"
  >
    <form @submit.prevent class="photo-upload-form">
      <!-- Drop zone -->
      <div
        class="photo-upload-form__file-area"
        :class="{ 'photo-upload-form__file-area--dragging': isDragging }"
        @click="() => fileInput?.click()"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
      >
        <input
          type="file"
          ref="fileInput"
          @change="handleFileChange"
          class="photo-upload-form__file-input"
          accept="image/*,video/*"
          multiple
        />
        <div class="photo-upload-form__file-icon">
          <i class="pi pi-images"></i>
        </div>
        <div class="photo-upload-form__file-text">
          <p class="photo-upload-form__file-title">
            {{
              previewItems.length > 0
                ? `${previewItems.length} ${$t('documents.filesSelected')}`
                : $t('projects.dropPhotosHere')
            }}
          </p>
          <p class="photo-upload-form__file-subtitle">{{ $t('projects.uploadFormatsHint') }}</p>
        </div>
      </div>

      <!-- File error -->
      <small v-if="fileError" class="photo-upload-form__error">{{ fileError }}</small>

      <!-- Thumbnail preview grid -->
      <div v-if="previewItems.length > 0" class="photo-upload-form__preview-grid">
        <div
          v-for="(item, index) in previewItems"
          :key="index"
          class="photo-upload-form__preview-item"
        >
          <img
            v-if="item.isImage"
            :src="item.url"
            class="photo-upload-form__preview-thumb"
            :alt="item.name"
          />
          <div v-else class="photo-upload-form__preview-video">
            <i class="pi pi-video"></i>
          </div>
          <button
            type="button"
            class="photo-upload-form__preview-remove"
            @click.stop="removeFile(index)"
            :aria-label="$t('documents.removeFile')"
          >
            <i class="pi pi-times"></i>
          </button>
          <span class="photo-upload-form__preview-name" :title="item.name">{{ item.name }}</span>
        </div>
      </div>

      <!-- Client visibility toggle (FULL_MANAGEMENT only) -->
      <div v-if="showClientVisibilityToggle" class="photo-upload-form__client-toggle">
        <span class="photo-upload-form__client-toggle-label">{{ $t('projects.shareWithClient') }}</span>
        <div class="photo-upload-form__client-toggle-control">
          <label class="toggle-switch">
            <input type="checkbox" v-model="sharedWithClient" />
            <span class="toggle-slider"></span>
          </label>
          <span
            class="photo-upload-form__toggle-hint"
            :class="sharedWithClient ? 'hint-visible' : 'hint-hidden'"
          >
            {{ sharedWithClient ? $t('projects.sharedWithClient') : $t('projects.hiddenFromClient') }}
          </span>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="photo-upload-form__actions">
        <AppButton type="button" variant="outline" @click="handleClose" :disabled="uploading">
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          @click="handleSubmit"
          :disabled="previewItems.length === 0 || uploading"
          :loading="uploading"
        >
          <i v-if="!uploading" class="pi pi-cloud-upload" aria-hidden="true"></i>
          {{ $t('documents.upload') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import AppButton from '../../base/AppButton.vue';
import AppModal from '../../base/Modal.vue';
import { sanitizeFileNames } from '@/utils/fileNameSanitizer';

interface Props {
  show: boolean;
  uploading?: boolean;
  showClientVisibilityToggle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  uploading: false,
  showClientVisibilityToggle: false,
});

interface Emits {
  (e: 'close'): void;
  (e: 'upload', payload: { files: File[]; sharedWithClient: boolean }): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();

interface PreviewItem {
  file: File;
  url: string;
  name: string;
  isImage: boolean;
}

const MAX_FILES = 10;

const previewItems = ref<PreviewItem[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const sharedWithClient = ref(true);
const fileError = ref('');

const addFiles = (files: File[]) => {
  const media = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
  const sanitized = sanitizeFileNames(media);
  const remaining = MAX_FILES - previewItems.value.length;
  const toAdd = sanitized.slice(0, remaining);
  if (sanitized.length > remaining) {
    fileError.value = t('documents.tooManyFiles', { max: MAX_FILES });
  }
  for (const file of toAdd) {
    previewItems.value.push({
      file,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      name: file.name,
      isImage: file.type.startsWith('image/'),
    });
  }
};

const removeFile = (index: number) => {
  const item = previewItems.value[index];
  if (item.url) URL.revokeObjectURL(item.url);
  previewItems.value.splice(index, 1);
  fileError.value = '';
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  addFiles(Array.from(target.files));
  target.value = '';
};

const handleDragOver = () => { isDragging.value = true; };
const handleDragLeave = () => { isDragging.value = false; };
const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  if (!event.dataTransfer) return;
  addFiles(Array.from(event.dataTransfer.files));
};

const handleSubmit = () => {
  if (previewItems.value.length === 0 || props.uploading) return;
  emit('upload', {
    files: previewItems.value.map(p => p.file),
    sharedWithClient: sharedWithClient.value,
  });
};

const resetForm = () => {
  for (const item of previewItems.value) {
    if (item.url) URL.revokeObjectURL(item.url);
  }
  previewItems.value = [];
  isDragging.value = false;
  sharedWithClient.value = true;
  fileError.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

watch(
  () => props.show,
  (newVal, oldVal) => { if (newVal && !oldVal) resetForm(); }
);

onBeforeUnmount(() => {
  for (const item of previewItems.value) {
    if (item.url) URL.revokeObjectURL(item.url);
  }
});

const handleClose = () => {
  resetForm();
  emit('close');
};
</script>

<style scoped>
/* Photo Upload Form */
.photo-upload-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-md, 1rem);
}

/* Drop zone */
.photo-upload-form__file-area {
  border: 2px dashed var(--border-medium, #dee2e6);
  border-radius: var(--radius-md, 8px);
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: var(--surface-secondary, #f8f9fa);
  text-align: center;
  position: relative;
}

.photo-upload-form__file-area:hover,
.photo-upload-form__file-area--dragging {
  border-color: var(--primary, #667eea);
  background-color: rgba(102, 126, 234, 0.04);
}

.photo-upload-form__file-input {
  display: none;
}

.photo-upload-form__error {
  display: block;
  color: var(--danger, #e53e3e);
  font-size: 0.8rem;
  margin-top: 4px;
}

.photo-upload-form__file-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--primary-light, #eef2ff);
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-upload-form__file-icon i {
  font-size: 1.25rem;
  color: var(--primary, #667eea);
}

.photo-upload-form__file-title {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: 500;
  color: var(--text-primary, #212529);
  margin: 0;
}

.photo-upload-form__file-subtitle {
  font-size: var(--font-size-xs, 0.75rem);
  color: var(--neutral-500, #6c757d);
  margin: 0;
}

.photo-upload-form__file-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Thumbnail preview grid */
.photo-upload-form__preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.25rem;
}

.photo-upload-form__preview-item {
  position: relative;
  border-radius: var(--radius-sm, 6px);
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--neutral-100, #f3f4f6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.25rem;
}

.photo-upload-form__preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.photo-upload-form__preview-video {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--neutral-400, #9ca3af);
}

.photo-upload-form__preview-video i {
  font-size: 1.75rem;
}

.photo-upload-form__preview-remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  transition: background-color 0.15s;
}

.photo-upload-form__preview-remove:hover {
  background: rgba(220, 38, 38, 0.85);
}

.photo-upload-form__preview-remove i {
  font-size: 0.625rem;
}

.photo-upload-form__preview-name {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 0.625rem;
  padding: 0.15rem 0.3rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Client visibility toggle — same pattern as DocumentUploadModal */
.photo-upload-form__client-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md, 1rem);
  padding: 0.625rem 0.875rem;
  background-color: var(--surface-secondary, #f8f9fa);
  border: 1px solid var(--border-light, #e9ecef);
  border-radius: var(--radius-md, 8px);
}

.photo-upload-form__client-toggle-label {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: 500;
  color: var(--text-primary, #212529);
  flex: 1;
}

.photo-upload-form__client-toggle-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.photo-upload-form__toggle-hint {
  font-size: var(--font-size-xs, 0.75rem);
  font-weight: 500;
  white-space: nowrap;
}

.photo-upload-form__toggle-hint.hint-visible {
  color: var(--success-600, #16a34a);
}

.photo-upload-form__toggle-hint.hint-hidden {
  color: var(--neutral-500, #6c757d);
}

/* Toggle switch (copy from DocumentUploadModal) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-300, #ced4da);
  border-radius: 22px;
  transition: background-color 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary, #667eea);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(18px);
}

/* Footer actions */
.photo-upload-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm, 0.5rem);
}
</style>
